import { useCallback, useEffect, useState } from "react";
import {
  CalendarPlus, ChevronLeft, ChevronRight, Copy, Loader2, MapPin, Rss, Trash2, Video,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Empty, Err, Ok, Panel } from "@/components/community/kit";
import { day, when } from "@/lib/community";

const TONE = {
  meeting: "bg-gold text-ink-900",
  nominations: "bg-sky-500 text-white",
  voting: "bg-sky-400 text-ink-900",
  motion: "bg-violet-500 text-white",
  poll: "bg-violet-400 text-ink-900",
  dues: "bg-red-500 text-white",
  grace: "bg-amber-500 text-ink-900",
  date: "bg-slate-400 text-ink-900",
};
const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ANSWERS = [["yes", "Going"], ["maybe", "Maybe"], ["no", "Can't"]];

const shift = (month, by) => {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const EventRow = ({ event, onGo, onRsvp, canRsvp, busy }) => (
  <div data-testid={`calendar-event-${event.id}`}
    className="rounded-xl border border-ink-600 bg-ink-900/60 p-3">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 shrink-0 rounded-full ${TONE[event.kind]}`} />
          <p className="truncate text-sm text-slate-100">{event.title}</p>
        </div>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
          {event.kind_label} · {event.all_day ? day(event.when) : when(event.when)}
        </p>
        {event.detail && <p className="mt-1 text-xs text-slate-400">{event.detail}</p>}
        {event.location && (
          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
            <MapPin className="h-3 w-3" /> {event.location}
          </p>
        )}
        {event.link && (
          <a href={event.link} target="_blank" rel="noreferrer"
            className="mt-1 flex items-center gap-1.5 text-xs text-gold hover:underline">
            <Video className="h-3 w-3" /> Join online
          </a>
        )}
      </div>
      <button data-testid={`calendar-go-${event.id}`} onClick={() => onGo(event.tab)}
        className="font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-gold">
        Open
      </button>
    </div>
    {event.kind === "meeting" && canRsvp && (
      <div className="mt-2.5 flex flex-wrap gap-2">
        {ANSWERS.map(([key, label]) => (
          <button key={key} data-testid={`calendar-rsvp-${key}-${event.id}`}
            disabled={busy} onClick={() => onRsvp(event, key)}
            className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${
              event.my_rsvp === key ? "border-gold/40 bg-gold/10 text-gold"
                : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
            {label} · {event.rsvps?.[key] || 0}
          </button>
        ))}
      </div>
    )}
  </div>
);

export const Calendar = ({ community, onTab }) => {
  const cid = community.id;
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState("");
  const [draft, setDraft] = useState({ title: "", on: "", note: "" });
  const [adding, setAdding] = useState(false);
  const [feed, setFeed] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/calendar`,
        { params: month ? { month } : {} });
      setData(res);
      setMonth(res.month);
    } catch (e) { setError(errMsg(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, month]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, key) => {
    setBusy(key); setError(""); setNotice("");
    try { await fn(); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const subscribe = async () => {
    setBusy("feed"); setError(""); setNotice("");
    try {
      const { data: res } = await api.post(`/communities/${cid}/calendar/feed`);
      setFeed(`${process.env.REACT_APP_BACKEND_URL}${res.path}`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  const byDate = {};
  data.events.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
  const cells = [...Array(data.first_weekday).fill(null),
    ...Array.from({ length: data.days }, (_, i) => i + 1)];
  const dateOf = (n) => `${data.month}-${String(n).padStart(2, "0")}`;
  const shown = picked ? (byDate[picked] || []) : data.upcoming;

  return (
    <div data-testid="community-calendar" className="space-y-4">
      <Err testId="calendar-error">{error}</Err>
      <Ok testId="calendar-notice">{notice}</Ok>

      <Panel testId="calendar-grid"
        title={data.label}
        action={
          <div className="flex items-center gap-1">
            <button data-testid="calendar-prev" onClick={() => { setPicked(""); setMonth(shift(data.month, -1)); }}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-ink-600 text-slate-400 hover:text-gold">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button data-testid="calendar-today" onClick={() => { setPicked(""); setMonth(data.today.slice(0, 7)); }}
              className="rounded-md border border-ink-600 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-gold">
              Today
            </button>
            <button data-testid="calendar-next" onClick={() => { setPicked(""); setMonth(shift(data.month, 1)); }}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-ink-600 text-slate-400 hover:text-gold">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        }>
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {WEEK.map((d) => (
            <p key={d} className="pb-1 text-center font-mono text-[9px] uppercase tracking-widest text-slate-600">
              {d.slice(0, 1)}<span className="hidden sm:inline">{d.slice(1)}</span>
            </p>
          ))}
          {cells.map((n, i) => {
            if (!n) return <div key={`pad-${i}`} />;
            const iso = dateOf(n);
            const dots = byDate[iso] || [];
            const isToday = iso === data.today;
            return (
              <button key={iso} data-testid={`calendar-day-${iso}`}
                onClick={() => setPicked(picked === iso ? "" : iso)}
                className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border transition-colors sm:aspect-auto sm:h-16 ${
                  picked === iso ? "border-gold bg-gold/10"
                    : isToday ? "border-gold/40 bg-ink-900"
                    : dots.length ? "border-ink-600 bg-ink-900/60 hover:border-gold/30"
                    : "border-transparent hover:border-ink-600"}`}>
                <span className={`text-xs ${isToday ? "font-semibold text-gold" : dots.length ? "text-slate-200" : "text-slate-600"}`}>
                  {n}
                </span>
                <span className="flex h-1.5 items-center gap-0.5">
                  {dots.slice(0, 4).map((e, j) => (
                    <span key={j} className={`h-1.5 w-1.5 rounded-full ${TONE[e.kind]}`} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5">
          {Object.entries(data.kinds).map(([kind, label]) => (
            <span key={kind} className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-slate-500">
              <span className={`h-1.5 w-1.5 rounded-full ${TONE[kind]}`} /> {label}
            </span>
          ))}
        </div>
      </Panel>

      <Panel testId="calendar-list"
        title={picked ? day(picked) : "Next 30 days"}
        action={picked && (
          <Button data-testid="calendar-clear-day" size="sm" variant="ghost"
            onClick={() => setPicked("")}>
            Back to the list
          </Button>
        )}>
        {shown.length === 0 ? (
          <Empty testId="calendar-empty">
            {picked ? "Nothing on this day." : "Nothing in the next 30 days."}
          </Empty>
        ) : (
          <div className="space-y-2.5">
            {shown.map((e) => (
              <EventRow key={e.id} event={e} canRsvp={data.can_rsvp} busy={busy === e.id}
                onGo={(tab) => onTab?.(tab === "calendar" ? "calendar" : tab)}
                onRsvp={(event, answer) => run(async () => {
                  await api.post(
                    `/communities/${cid}/meetings/${event.id.replace("meeting-", "")}/rsvp`,
                    { answer });
                }, e.id)} />
            ))}
          </div>
        )}
      </Panel>

      {data.can_add && (
        <Panel testId="calendar-add" title="Pin a date"
          sub="A deadline that isn't a meeting — permit renewals, filing dates, anything members must not miss."
          action={
            <Button data-testid="calendar-add-toggle" size="sm" variant="outline"
              onClick={() => setAdding((v) => !v)}>
              {adding ? "Cancel" : <><CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Add a date</>}
            </Button>
          }>
          {adding && (
            <div className="space-y-3">
              <Input data-testid="date-title" placeholder="Airport permit renewal due"
                value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input data-testid="date-on" type="date" value={draft.on}
                  onChange={(e) => setDraft({ ...draft, on: e.target.value })} />
                <Input data-testid="date-note" placeholder="Note (optional)" value={draft.note}
                  onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
              </div>
              <Button data-testid="date-save"
                disabled={draft.title.trim().length < 2 || draft.on.length !== 10 || busy === "date"}
                onClick={() => run(async () => {
                  await api.post(`/communities/${cid}/calendar/dates`, draft);
                  setDraft({ title: "", on: "", note: "" });
                  setAdding(false);
                  setNotice("Pinned to the calendar.");
                }, "date")}>
                {busy === "date" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Pin it
              </Button>
            </div>
          )}
          {data.events.filter((e) => e.kind === "date").length > 0 && (
            <ul className="mt-3 divide-y divide-ink-600">
              {data.events.filter((e) => e.kind === "date").map((e) => (
                <li key={e.id} data-testid={`pinned-date-${e.id}`}
                  className="flex items-center justify-between gap-3 py-2">
                  <p className="truncate text-sm text-slate-300">{e.title} · {day(e.when)}</p>
                  <button data-testid={`date-delete-${e.id}`}
                    onClick={() => run(async () => {
                      await api.delete(
                        `/communities/${cid}/calendar/dates/${e.id.replace("date-", "")}`);
                    }, e.id)}
                    className="text-slate-500 hover:text-red-300">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      {data.can_rsvp && (
        <Panel testId="calendar-subscribe" title="Put this in your phone calendar"
          sub="Meetings, votes and your dues date show up next to your work — it keeps itself up to date.">
          {feed ? (
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input data-testid="feed-url" readOnly value={feed} className="font-mono text-xs" />
                <Button data-testid="feed-copy" variant="outline"
                  onClick={() => { navigator.clipboard?.writeText(feed); setNotice("Link copied."); }}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <a data-testid="feed-open" href={feed.replace(/^https?:/, "webcal:")}
                className="inline-flex items-center gap-1.5 text-sm text-gold hover:underline">
                <Rss className="h-3.5 w-3.5" /> Add it now
              </a>
              <p className="text-xs text-slate-500">
                On iPhone that link opens Calendar directly. On Google Calendar use
                <span className="text-slate-400"> Other calendars → From URL</span> and paste it.
                This link is yours alone — generating a new one turns the old one off.
              </p>
            </div>
          ) : (
            <Button data-testid="feed-create" onClick={subscribe} disabled={busy === "feed"}>
              {busy === "feed" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Rss className="mr-2 h-4 w-4" />}
              {data.feed_ready ? "Show my calendar link" : "Add to my phone calendar"}
            </Button>
          )}
        </Panel>
      )}
    </div>
  );
};
